/*----------------------------------
- HOME WORKFLOW BENTO
- "Unique Domains helps you decide." 6-tile bento.
- Every mock UI uses illustrative fake data only (aria-hidden), matching the
- redesign mockup 1:1.
----------------------------------*/

import React from 'react';

import CtaPill from '@uniquedomains/components/website/WebsiteCta';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';
import { PAGE_CONTAINER_CLASS } from '@/client/pages/landing/components/shared/pageStyles';

import BuyerWorkflowMotion from './BuyerWorkflowMotion';
import OpportunitiesWorkflowMotion from './OpportunitiesWorkflowMotion';
import SaveWorkflowMotion from './SaveWorkflowMotion';
import SearchWorkflowMotion from './SearchWorkflowMotion';
import TrackWorkflowMotion from './TrackWorkflowMotion';
import VerifySignalsMotion from './VerifySignalsMotion';
import { START_WITH_BRIEF_LABEL } from '@/client/catalogs/landing/accessOffer';
import './workflowMotion.css';

const SECTION_EYEBROW_CLASS = 'text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600';
/* White bordered cards in the phone's tinted panel, the shipped flat #F8F7F5 tiles
   from `lg`. The border is removed rather than recoloured at `lg` so the desktop
   content box keeps the exact size it ships with. */
const TILE_CLASS =
    'flex flex-col rounded-[22px] border border-[#ebe7de] bg-white p-[26px] lg:rounded-[28px] lg:border-0 lg:bg-[#F8F7F5]';
const MOTION_TILE_CLASS =
    'flex flex-col overflow-hidden rounded-[22px] border border-[#ebe7de] bg-white lg:rounded-[28px] lg:border-0 lg:bg-[#F8F7F5]';
const TILE_TITLE_CLASS = 'mt-2 text-[19px] font-semibold leading-[1.2] tracking-[-0.015em] text-slate-900';
const TILE_TITLE_LINK_CLASS =
    'rounded-sm underline-offset-4 transition hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400';
const TILE_BODY_CLASS = 'mt-2 text-sm leading-[1.6] text-slate-600';
const COMING_SOON_CLASS = 'inline-flex rounded-full bg-[#E8E5DD] px-[9px] py-[3px] text-[10px] font-bold uppercase tracking-[0.06em] text-[#5C5F61]';

/*----------------------------------
- COMPONENT
----------------------------------*/

type HomeWorkflowBentoProps = {
    ctaHref: string;
    onCtaClick?: (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
};

const HomeWorkflowBento = ({ ctaHref, onCtaClick }: HomeWorkflowBentoProps) => (
    <section id="product" class={`${PAGE_CONTAINER_CLASS} pt-[72px] lg:pt-32`}>
        <div class="lg:mx-auto lg:max-w-[680px] lg:text-center">
            <h2 class="text-pretty text-[30px] font-medium leading-[1.1] tracking-[-0.03em] text-slate-900 lg:text-balance lg:text-[clamp(2.125rem,3.2vw,2.875rem)]">
                Unique Domains helps you decide.
            </h2>
            <p class="mt-4 text-base leading-[1.6] text-slate-600 lg:mx-auto lg:mt-[18px] lg:text-[16.5px] lg:leading-[1.65]">
                Does this domain fit my criteria? Can I own it? Will I regret it? One platform to answer these three
                questions, so you buy with a reason, not a hunch.
            </p>
        </div>

        <div class="ud-landing-card-shell mt-7 grid grid-cols-12 lg:mt-[52px]">
            {/* 1 · Search */}
            <article data-reveal class={`${MOTION_TILE_CLASS} col-span-12 lg:col-span-7`}>
                <SearchWorkflowMotion />
                <div class="px-[26px] pb-[26px]">
                    <div class={`mt-[22px] ${SECTION_EYEBROW_CLASS}`}>1 · Search</div>
                    <h3 class={TILE_TITLE_CLASS}>
                        <a href={LANDING_WEBSITE_PAGE_ROUTES.productSearch} class={TILE_TITLE_LINK_CLASS}>
                            Search deeper than marketplaces can show you.
                        </a>
                    </h3>
                    <p class={TILE_BODY_CLASS}>
                        Browse the one-word market through filters built for real decisions: price, renewal, TLD, sector,
                        word type, sentiment, demand, brandability, popularity, and risk.
                    </p>
                </div>
            </article>

            {/* 2 · Trending Domains, the daily edition (ADR-0074) */}
            <article
                data-reveal
                data-testid="home-workflow-opportunities"
                class={`${MOTION_TILE_CLASS} col-span-12 lg:col-span-5`}
            >
                <OpportunitiesWorkflowMotion />
                <div class="px-[26px] pb-[26px]">
                    <div class={`mt-[22px] ${SECTION_EYEBROW_CLASS}`}>2 · Trending Domains</div>
                    <h3 class={TILE_TITLE_CLASS}>
                        <a href={LANDING_WEBSITE_PAGE_ROUTES.trendingDomains} class={TILE_TITLE_LINK_CLASS}>
                            Review fresh domain leads from worldwide trends.
                        </a>
                    </h3>
                    {/* The tile links the investors' page, whose rows carry no business ideas (ADR-0074). */}
                    <p class={TILE_BODY_CLASS}>
                        Every day at 06:00 UTC, a dated edition ties rising search trends to one-word names you
                        can buy, with status, price, renewal and a verdict for each.
                    </p>
                </div>
            </article>

            {/* 3 · Verify */}
            <article
                data-reveal
                class={`${MOTION_TILE_CLASS} col-span-12 lg:grid lg:grid-cols-[1.35fr_0.85fr] lg:items-stretch`}
            >
                <div class="min-w-0">
                    <VerifySignalsMotion />
                </div>
                <div class="px-[26px] pb-[26px] lg:flex lg:flex-col lg:justify-center lg:py-[26px] lg:pl-0 lg:pr-[32px]">
                    <div class={`mt-[22px] lg:mt-0 ${SECTION_EYEBROW_CLASS}`}>3 · Verify</div>
                    <h3 class={TILE_TITLE_CLASS}>
                        <a href={LANDING_WEBSITE_PAGE_ROUTES.productVerify} class={TILE_TITLE_LINK_CLASS}>
                            Know why a name is worth buying.
                        </a>
                    </h3>
                    <p class={TILE_BODY_CLASS}>
                        Review ask price, renewal, estimated value, liquidity, variants, history, SEO signals, a trademark
                        check, and registrar path in one place.
                    </p>
                </div>
            </article>

            {/* 4 · Save. Tiles 4 and 5 split their row in halves since ADR-0081 removed the tile that made it thirds. */}
            <article data-reveal class={`${TILE_CLASS} col-span-12 sm:col-span-6`}>
                <SaveWorkflowMotion />
                <div class={`mt-[22px] ${SECTION_EYEBROW_CLASS}`}>4 · Save</div>
                <h3 class={TILE_TITLE_CLASS}>
                    <a href={LANDING_WEBSITE_PAGE_ROUTES.productRadars} class={TILE_TITLE_LINK_CLASS}>
                        Save your searches. Get daily fresh matches.
                    </a>
                </h3>
                <p class={TILE_BODY_CLASS}>
                    Turn a useful search into a Radar. Unique Domains saves the filters, sort logic, intent, and alert
                    cadence, then brings back fresh matching opportunities.
                </p>
            </article>

            {/* 5 · Track */}
            <article data-reveal class={`${TILE_CLASS} col-span-12 sm:col-span-6`}>
                <TrackWorkflowMotion />
                <div class={`mt-[22px] ${SECTION_EYEBROW_CLASS}`}>5 · Track</div>
                <h3 class={TILE_TITLE_CLASS}>
                    <a href={LANDING_WEBSITE_PAGE_ROUTES.productWatchlists} class={TILE_TITLE_LINK_CLASS}>
                        Keep the names that matter in a live shortlist.
                    </a>
                </h3>
                <p class={TILE_BODY_CLASS}>
                    Track price drops, ending-soon auctions, status changes, valuation shifts, risk flags, notes, tags,
                    and review state from one Watchlist.
                </p>
            </article>

            {/* 6 · Find buyers (horizontal) */}
            <article data-reveal class={`${TILE_CLASS} col-span-12 gap-6 lg:grid lg:grid-cols-[1fr_1.05fr] lg:items-center lg:gap-[30px]`}>
                <div class="min-w-0">
                    <div class="flex items-center gap-2">
                        <span class={SECTION_EYEBROW_CLASS}>6 · Find buyers</span>
                        <span class={COMING_SOON_CLASS}>Coming soon</span>
                    </div>
                    <h3 class="mt-2.5 text-[19px] font-semibold leading-[1.2] tracking-[-0.015em] text-slate-900">
                        Turn the names you own into targeted buyer lists.
                    </h3>
                    {/* Future tense on purpose: none of this is built yet. */}
                    <p class={TILE_BODY_CLASS}>
                        Unique Domains will find likely buyers for a domain you own, explain why each one fits, and
                        prepare a contact path with an outreach angle, so selling starts with a focused list rather
                        than cold guesswork.
                    </p>
                </div>
                <div class="mt-6 min-w-0 lg:mt-0">
                    <BuyerWorkflowMotion />
                </div>
            </article>
        </div>

        {/* `CtaPill` sizes itself to its label, so the phone's full-bleed pill is
            expressed on the row that owns it rather than by a width prop here. */}
        <div class="mt-[18px] flex [&>a]:w-full lg:mt-10 lg:justify-center lg:[&>a]:w-fit">
            <CtaPill href={ctaHref} variant="accent" size="lg" onClick={onCtaClick}>
                {START_WITH_BRIEF_LABEL}
            </CtaPill>
        </div>
    </section>
);

export default HomeWorkflowBento;
